/**
 * MapFilter component for hotel/city search, date selection, room and guest configuration, and promo code input.
 * Handles complex logic for updating room and guest counts, date range selection, and modal interactions.
 */
import * as React from "react";
import * as v from "valibot";
import { Controller, useFormContext } from "react-hook-form";
import { MapPin } from "lucide-react";
import { useState } from "react";
import { DateRange } from "react-day-picker";
import { format } from "date-fns";
import {
  AGE_OPTIONS,
  datePickerFormatJP,
  DEFAULT_ADULTS,
  DEFAULT_CHECKIN_DATE,
  DEFAULT_CHECKOUT_DATE,
} from "@/constants";
import { useModal } from "@/hooks/useModal/useModal";

import { iconList } from "@/assets/icons";

// Spinner loading message for CustomSelect
const CustomSelectLoadingMessage = () => (
  <iconList.Spinner
    className="m-auto mt-5 h-10 w-10 animate-spin text-gray-200 dark:text-gray-600"
    fill={"var(--fill-gray-stronger)"}
  />
);

import useSearchFormStore from "@/store/searchFormStore/searchFormStore";
import {
  cityAndHotelFuzzyFilter,
  SelectOption as UtilSelectOption,
} from "@/utils";
import useJapanAreasStore from "@/store/hooks/useGlobal";
import { RoomSelect } from "../../search-avaliable/RoomSelect";
import {
  CustomSearchAvailableProps,
  schemaSearchAvailableRoom,
} from "../../search-avaliable/SearchAvailableRoomMobile";
import {
  CONFIG_SEARCH_AVAILABLE_ROOM,
  deepCloneRoom,
  TRoom,
} from "../../search-avaliable/ultils";
import { getSearchRoomData } from "../../hotel-result/filter/utils";
import { useParams } from "next/navigation";
import { GroupBase } from "react-select";
import { cn } from "@/lib/utils";
import { CustomSelect } from "@/components/atoms/dropdown";
import { DateRangePicker } from "@/components/atoms/date-range-picker";
import { Button } from "@/components/atoms/button";
import { Modal } from "@/components/atoms/modal";import { Input } from "@/components/atoms/input";

export interface CustomSearchAvailableExtendProps
  extends CustomSearchAvailableProps {
  range: DateRange;
  setRange: React.Dispatch<React.SetStateAction<DateRange>>;
}
export type IMapFilterForm = v.InferOutput<typeof schemaSearchAvailableRoom>;

const MapFilter: React.FC<CustomSearchAvailableExtendProps> = ({
  listOption,
  range,
  setRange,
}) => {
  const [inputValue, setInputValue] = useState<string>("");
  const { configurations: configSearchAvailableRoom, formData, setFormData } = useSearchFormStore();
  const params = useParams();
  const { isOpen, toggleModal } = useModal();
  const searchRoomData = getSearchRoomData(configSearchAvailableRoom, params);

  const {
    configurations,
    rooms_configurations,
    ages_configurations,
    tripla_section,
  } = searchRoomData || {};
  const {
    checkin_label,
    checkout_label,
    promo_code_label,
    promo_code_placeholder,
  } = configurations ?? {};
  const {
    adult_label,
    children_label,
    maximum_adults,
    maximum_child_age,
    maximum_children,
    maximum_rooms,
    single_room_label,
    default_adult_count,
  } = rooms_configurations ?? {};
  const {
    tripla_add_room_label,
    tripla_age_label,
    tripla_apply_label,
    tripla_bed_label,
    tripla_child_label,
    tripla_room_adults_label,
    tripla_room_children_label,
    tripla_room_name_label,
    tripla_with_bed_label,
    tripla_without_bed_label,
  } = tripla_section ?? {};
  const { age_placeholder } = ages_configurations ?? {};
  const [previousRange, setPreviousRange] = useState<DateRange>({
    from: new Date(DEFAULT_CHECKIN_DATE),
    to: new Date(DEFAULT_CHECKOUT_DATE),
  });
  const { control, formState, watch, setValue } =
    useFormContext<IMapFilterForm>();
  /**
   * Handles date range selection logic, including updating previous range and form values.
   */
  const handleRangeSelect = (
    nextRange: DateRange | undefined,
    selectedDay: Date | undefined,
  ) => {
    // Save previous range for cancel functionality
    if (range.from && range.to) {
      setPreviousRange({ from: range.from, to: range.to });
    }

    if (nextRange) {
      // If user already has a complete range (both from and to), reset and start new selection
      if (range.from && range.to && selectedDay) {
        setRange({ from: selectedDay });
        setValue("checkIn", selectedDay);
        setValue("checkout", null);
      } else {
        // Normal range selection behavior
        setRange(nextRange);
        
        // Update form values
        if (nextRange.from) {
          setValue("checkIn", nextRange.from);
        }
        if (nextRange.to) {
          setValue("checkout", nextRange.to);
        }
      }
    } else if (!selectedDay) {
      // Reset to defaults if no range
      setRange({
        from: new Date(DEFAULT_CHECKIN_DATE),
        to: new Date(DEFAULT_CHECKOUT_DATE),
      });
    }
  };

  const { loading } = useJapanAreasStore();

  const rooms = watch("rooms");
  const MAXIMUM_ADULTS_LIMIT = maximum_adults
    ? Number(maximum_adults)
    : CONFIG_SEARCH_AVAILABLE_ROOM.MAXIMUM_ADULTS_LIMIT;
  const MAXIMUM_CHILDREN_LIMIT = maximum_children
    ? Number(maximum_children)
    : CONFIG_SEARCH_AVAILABLE_ROOM.MAXIMUM_CHILDREN_LIMIT;
  const MAXIMUM_ROOMS_LIMIT = maximum_rooms
    ? Number(maximum_rooms)
    : CONFIG_SEARCH_AVAILABLE_ROOM.MAXIMUM_ROOMS_LIMIT;
  const adultsCount = rooms?.[0]?.adults || Number(DEFAULT_ADULTS);
  const childrenCount = rooms?.[0]?.children?.length || 0;
  const canIncreaseAdults = adultsCount < MAXIMUM_ADULTS_LIMIT;
  const canDecreaseAdults = adultsCount > 1;
  const canIncreaseChildren = childrenCount < MAXIMUM_CHILDREN_LIMIT;
  const canDecreaseChildren = childrenCount > 0;

  /**
   * Increment adults count in the first room, respecting maximum limit.
   */
  const incrementAdults = () => {
    if (rooms?.[0] && canIncreaseAdults) {
      const newRooms = deepCloneRoom([...(rooms as TRoom[])]);
      if (!newRooms[0]) newRooms[0] = { adults: 0, children: [] };
      newRooms[0] = { ...newRooms[0], adults: (newRooms[0].adults || 0) + 1 };
      setValue("rooms", newRooms);
      setFormData({ ...formData, rooms: newRooms } as typeof formData & { rooms: TRoom[] });
    }
  };

  /**
   * Decrement adults count in the first room, respecting minimum limit.
   */
  const decrementAdults = () => {
    if (rooms?.[0] && canDecreaseAdults) {
      const newRooms = deepCloneRoom([...(rooms as TRoom[])]);
      if (newRooms[0]) {
        // Extract assignment
        newRooms[0].adults = newRooms[0].adults - 1;

        newRooms[0] = {
          adults: newRooms[0].adults,
          children: newRooms[0].children,
        };
      }

      setValue("rooms", newRooms);
      setFormData({ ...formData, rooms: newRooms } as typeof formData & { rooms: TRoom[] });
    }
  };

  /**
   * Increment children count in the first room, adding a child with default age and bed status.
   */
  const incrementChildren = () => {
    if (rooms?.[0] && canIncreaseChildren) {
      const updatedRoomsForIncrement = deepCloneRoom([...(rooms as TRoom[])]);

      let firstRoom = updatedRoomsForIncrement[0];

      if (!firstRoom) {
        firstRoom = { adults: Number(DEFAULT_ADULTS), children: [] };
        updatedRoomsForIncrement[0] = firstRoom;
      }

      const newChildrenArray = [...(firstRoom.children || [])];
      newChildrenArray.push({ age: AGE_OPTIONS[0], isBed: false });
      firstRoom.children = newChildrenArray;

      setValue("rooms", updatedRoomsForIncrement);
      setFormData({ ...formData, rooms: updatedRoomsForIncrement } as typeof formData & { rooms: TRoom[] });
    }
  };

  /**
   * Decrement children count in the first room, removing the last child from the array.
   */
  const decrementChildren = () => {
    if (rooms?.[0] && canDecreaseChildren) {
      const updatedRoomsForDecrement = deepCloneRoom([...(rooms as TRoom[])]);

      const firstRoom = updatedRoomsForDecrement[0];

      if (firstRoom?.children && firstRoom?.children?.length > 0) {
        const newChildrenArray = [...firstRoom.children];
        newChildrenArray.pop();
        firstRoom.children = newChildrenArray;
      }

      setValue("rooms", updatedRoomsForDecrement);
      setFormData({ ...formData, rooms: updatedRoomsForDecrement } as typeof formData & { rooms: TRoom[] });
    }
  };

  return (
    <div className="relative rounded-lg bg-white">
      <div className="flex w-full flex-col gap-1.5 md:gap-3 lg:px-6">
        <div className="flex w-full flex-col gap-4 md:flex-row lg:gap-5">
          <div className="relative flex flex-1 flex-col gap-1">
            <div className="font text-[13px] text-[var(--text-base-primary)]">
              エリア名／ホテル名
            </div>
            <Controller
              name="hotelOrCity"
              control={control}
              render={({ field }) => (
                <CustomSelect<
                  UtilSelectOption,
                  false,
                  GroupBase<UtilSelectOption>
                >
                  value={field.value}
                  options={listOption}
                  onChange={(newValue) => {
                    field.onChange(newValue);
                    // Priority order for adult count on hotel/city change:
                    // 1. formData.rooms exists (user previously changed) → restore their count
                    // 2. newValue.initial_adult_count (per-hotel CMS field)
                    // 3. default_adult_count (global CMS config)
                    // 4. DEFAULT_ADULTS (hardcoded fallback)
                    const userHasSavedRooms =
                      !!formData?.rooms && formData.rooms.length > 0;

                    if (userHasSavedRooms) {
                      setValue("rooms", formData!.rooms as TRoom[]);
                    } else if (newValue?.type === "hotel") {
                      const configuredCount =
                        Number(newValue?.initial_adult_count) ||
                        Number(default_adult_count) ||
                        Number(DEFAULT_ADULTS);
                      const currentRooms = deepCloneRoom([...(rooms as TRoom[])]);
                      if (currentRooms[0]) {
                        currentRooms[0] = { ...currentRooms[0], adults: configuredCount };
                      }
                      setValue("rooms", currentRooms);
                    } else if (newValue?.type === "city") {
                      const configuredCount =
                        Number(default_adult_count) ||
                        Number(DEFAULT_ADULTS);
                      const currentRooms = deepCloneRoom([...(rooms as TRoom[])]);
                      if (currentRooms[0]) {
                        currentRooms[0] = { ...currentRooms[0], adults: configuredCount };
                      }
                      setValue("rooms", currentRooms);
                    }
                  }}
                  onInputChange={setInputValue}
                  inputValue={inputValue}
                  showAllButton={inputValue?.length > 0}
                  onShowAll={() => {
                    setInputValue("");
                  }}
                  placeholder="エリア名／ホテル名を入力してください"
                  loadingMessage={CustomSelectLoadingMessage}
                  isLoading={loading}
                  error={formState.errors.hotelOrCity?.value?.message}
                  className="w-full"
                  size="md"
                  iconCustom={
                    <MapPin
                      strokeWidth={1}
                      width={18}
                      height={18}
                      className="text-[#7F8498]"
                    />
                  }
                  filterOption={(option, inputValue) => {
                    return cityAndHotelFuzzyFilter(
                      { label: option.label, data: option.data },
                      inputValue,
                    );
                  }}
                  styles={{
                    control: (base) => ({
                      ...base,
                      paddingLeft: "12px",
                      paddingRight: "12px",
                      padding: "8px",
                      fontSize: "13px",
                      borderRadius: "var(--radius-input-md)",
                      maxHeight: "43px",
                      cursor: "text",
                    }),
                    placeholder: (base) => ({
                      ...base,
                      fontSize: "13px",
                      color: "var(--text-base-primary)",
                      opacity: 0.6,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }),

                    singleValue: (base) => ({
                      ...base,
                      fontSize: "13px",
                      color: "var(--text-gray-stronger)",
                    }),
                    option: (base) => ({
                      ...base,
                      fontSize: "13px",
                      color: "var(--text-gray-stronger)",
                      cursor: "pointer",
                    }),
                    groupHeading: (base) => ({
                      ...base,
                      fontSize: "14px",
                      fontWeight: "bold",
                      color: "var(--text-gray-stronger)",
                      padding: "8px 12px",
                    }),

                    menuList(base) {
                      return {
                        ...base,
                        maxHeight: "250px",
                      };
                    },
                  }}
                />
              )}
            />
          </div>
          <div className="flex flex-1 flex-col gap-1">
            <div className="text-[13px] text-[var(--text-base-primary)]">
              {checkin_label} - {checkout_label}
            </div>
            <DateRangePicker
              range={range}
              onChange={handleRangeSelect}
              onCancel={() => {
                setRange(previousRange);
              }}
            >
              <Button
                hierarchy="outline"
                variant="neutral"
                type="button"
                size={"xl"}
                className="flex max-h-[43px] w-full items-center justify-around rounded-md border border-gray-300 px-3 py-2 text-[13px] text-[var(--text-base-primary)]"
              >
                <span>
                  {range?.from
                    ? format(range.from, datePickerFormatJP)
                    : checkin_label}
                </span>
                <span className="mx-2">→</span>
                <span>
                  {range?.to
                    ? format(range.to, datePickerFormatJP)
                    : checkout_label}
                </span>
              </Button>
            </DateRangePicker>
          </div>
        </div>

        <div className="flex w-full flex-col gap-4 md:flex-row lg:gap-5">
          <div className="xs-375:justify-around hidden items-center justify-between gap-2 md:flex md:gap-5">
            <div className="md:items-normal flex flex-col items-start">
              <div className="text-[13px] text-[var(--text-base-primary)]">
                {adult_label}
              </div>
              <div className="mt-auto mb-1 flex items-center">
                <Button
                  hierarchy={canDecreaseAdults ? "filled" : "outline"}
                  type="button"
                  className={cn(
                    "flex h-9 w-9 items-center justify-center rounded-md border-none",
                    {
                      "pointer-events-none bg-[var(--bg-map-section-button-disabled)] text-[var(--text-map-section-button-disabled)]":
                        !canDecreaseAdults,
                      "bg-[var(--bg-map-section-button)] text-[var(--text-base-primary)] hover:bg-[var(--bg-map-section-button)]":
                        canDecreaseAdults,
                    },
                  )}
                  onClick={decrementAdults}
                >
                  <span className="text-xl">-</span>
                </Button>
                <div className="mx-2 w-5 text-center">{adultsCount}</div>
                <Button
                  hierarchy={canIncreaseAdults ? "filled" : "outline"}
                  type="button"
                  className={cn(
                    "flex h-9 w-9 items-center justify-center rounded-md border-none",
                    {
                      "pointer-events-none bg-[var(--bg-map-section-button-disabled)] text-[var(--text-map-section-button-disabled)]":
                        !canIncreaseAdults,
                      "bg-[var(--bg-map-section-button)] text-[var(--text-base-primary)] hover:bg-[var(--bg-map-section-button)]":
                        canIncreaseAdults,
                    },
                  )}
                  onClick={incrementAdults}
                >
                  <span className="text-xl">+</span>
                </Button>
              </div>
            </div>
            <div className="md:items-normal flex flex-col items-start">
              <div className="text-[13px] text-[var(--text-base-primary)]">
                {children_label}
              </div>
              <div className="mt-auto mb-1 flex items-center">
                <Button
                  hierarchy={canDecreaseChildren ? "filled" : "outline"}
                  type="button"
                  className={cn(
                    "flex h-9 w-9 items-center justify-center rounded-md border-none",
                    {
                      "pointer-events-none bg-[var(--bg-map-section-button-disabled)] text-[var(--text-map-section-button-disabled)]":
                        !canDecreaseChildren,
                      "bg-[var(--bg-map-section-button)] text-[var(--text-base-primary)] hover:bg-[var(--bg-map-section-button)]":
                        canDecreaseChildren,
                    },
                  )}
                  onClick={decrementChildren}
                >
                  <span className="text-xl">-</span>
                </Button>
                <div className="mx-2 w-5 text-center">{childrenCount}</div>
                <Button
                  hierarchy={canIncreaseChildren ? "filled" : "outline"}
                  type="button"
                  className={cn(
                    "flex h-9 w-9 items-center justify-center rounded-md border-none",
                    {
                      "pointer-events-none bg-[var(--bg-map-section-button-disabled)] text-[var(--text-map-section-button-disabled)]":
                        !canIncreaseChildren,
                      "bg-[var(--bg-map-section-button)] text-[var(--text-base-primary)] hover:bg-[var(--bg-map-section-button)]":
                        canIncreaseChildren,
                    },
                  )}
                  onClick={incrementChildren}
                >
                  <span className="text-xl">+</span>
                </Button>
              </div>
            </div>
          </div>
          <div className="flex flex-1 flex-col gap-4 text-[var(--text-base-primary)] md:flex-row md:gap-5">
            <div className="flex flex-col items-center">
              <div className="hidden text-[13px] whitespace-nowrap text-[var(--text-base-primary)] md:block">
                {single_room_label}
              </div>
              <div className="mt-auto">
                <Controller
                  name="rooms"
                  control={control}
                  render={({ field }) => {
                    const roomCount = field.value?.length || 1;

                    return (
                      <>
                        <div className="hidden w-full md:block">
                          {/* Button to open room selection modal */}
                          <Button
                            variant="neutral"
                            hierarchy="outline"
                            size="xl"
                            type="button"
                            className="flex max-h-[43px] w-full items-center justify-between rounded-md border border-gray-300 px-3 py-2 text-[13px] text-[var(--text-base-primary)]"
                            onClick={toggleModal}
                          >
                            {`${roomCount} ${single_room_label || "部屋"}`}
                          </Button>

                          {/* Modal for selecting room configuration */}
                          <Modal
                            open={isOpen}
                            toggleModal={toggleModal}
                            containerClassName="rounded-2xl !max-w-[450px] gap-0"
                            closeClassName="bg-[var(--fill-base-main)] text-white rounded-full opacity-100 p-1.5"
                            header={
                              <div className="border-b border-b-[var(--stroke-gray-light)] px-4 pt-4 pb-3 text-left text-xl leading-8 text-[var(--text-gray-stronger)]">
                                部屋選択
                              </div>
                            }
                            footer={
                              <div className="w-full border-t border-t-[var(--stroke-gray-light)] px-4 pt-3 pb-4">
                                {/* Button to apply room selection and close modal */}
                                <Button
                                  variant="secondary"
                                  className="flex w-full items-center justify-between"
                                  size="lg"
                                  type="button"
                                  onClick={toggleModal}
                                >
                                  <span>{tripla_apply_label}</span>
                                </Button>
                              </div>
                            }
                          >
                            {/* RoomSelect component for configuring rooms and guests */}
                            <div className="max-h-[500px] overflow-y-auto px-4 pt-3 pb-4">
                              <RoomSelect
                                roomConfiguration={{
                                  adultLabel:
                                    tripla_room_adults_label ?? undefined,
                                  childrenLabel:
                                    tripla_room_children_label ?? undefined,
                                  addRoomButtonText:
                                    tripla_add_room_label ?? undefined,
                                  maximumAdults: String(MAXIMUM_ADULTS_LIMIT),
                                  maximumChildAge:
                                    maximum_child_age ?? undefined,
                                  maximumChildren: String(
                                    MAXIMUM_CHILDREN_LIMIT,
                                  ),
                                  maximumRooms: String(MAXIMUM_ROOMS_LIMIT),
                                  singleRoomLabel:
                                    tripla_room_name_label ?? undefined,
                                  bedLabel: tripla_bed_label ?? undefined,
                                  withBedLabel:
                                    tripla_with_bed_label ?? undefined,
                                  withoutBedLabel:
                                    tripla_without_bed_label ?? undefined,
                                }}
                                AgesConfigurations={{
                                  agesLabel: tripla_age_label ?? undefined,
                                  agePlaceholder: age_placeholder ?? undefined,
                                  singleChildLabel:
                                    tripla_child_label ?? undefined,
                                }}
                                isStoreForm={false}
                              />
                            </div>
                          </Modal>
                        </div>
                        {/* Inline RoomSelect for mobile view */}
                        <div className="mt-4 w-full md:hidden">
                          <RoomSelect
                            roomConfiguration={{
                              adultLabel: tripla_room_adults_label ?? undefined,
                              childrenLabel:
                                tripla_room_children_label ?? undefined,
                              addRoomButtonText:
                                tripla_add_room_label ?? undefined,
                              maximumAdults: String(MAXIMUM_ADULTS_LIMIT),
                              maximumChildAge: maximum_child_age ?? undefined,
                              maximumChildren: String(MAXIMUM_CHILDREN_LIMIT),
                              maximumRooms: String(MAXIMUM_ROOMS_LIMIT),
                              singleRoomLabel:
                                tripla_room_name_label ?? undefined,
                              bedLabel: tripla_bed_label ?? undefined,
                              withBedLabel: tripla_with_bed_label ?? undefined,
                              withoutBedLabel:
                                tripla_without_bed_label ?? undefined,
                            }}
                            AgesConfigurations={{
                              agesLabel: tripla_age_label ?? undefined,
                              agePlaceholder: age_placeholder ?? undefined,
                              singleChildLabel: tripla_child_label ?? undefined,
                            }}
                            isStoreForm={false}
                          />
                        </div>
                      </>
                    );
                  }}
                />
              </div>
            </div>

            <div className="col-span-1 flex w-full flex-col items-start">
              <div className="text-[13px] whitespace-nowrap text-[var(--text-base-primary)]">
                {promo_code_label}
              </div>
              <Controller
                name="promoCode"
                control={control}
                render={({ field }) => (
                  <Input
                    className="text-md mt-auto max-h-[43px] w-full rounded-md border border-gray-300 bg-white py-[12px] text-[var(--text-base-primary)] placeholder:text-sm"
                    value={field.value || ""}
                    size={"lg"}
                    onChange={(e) => {
                      field.onChange(e);
                    }}
                    placeholder={promo_code_placeholder || ""}
                  />
                )}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export { MapFilter };
